import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DirectusService } from '../core/services/directus.service';
import { ImageCropUploadComponent, CropsRecord, ImageCropResult } from '../shared/image-crop-upload.component';

interface Board {
  id: number;
  year: number;
  tagline: string | null;
  tagline_image: string | null;
}

interface BoardMember {
  id: number;
  name: string;
  position: string;
  image: string | null;
  profileAvatar: string | null;       // fallback image UUID from presidents
  profileAvatarCrops: CropsRecord | null; // fallback crops from presidents
  crops: CropsRecord | null;
  rank: number;
  lbod_id: number;
  profile_id: string | null;
  description: string | null;
}

interface MemberForm {
  name: string;
  position: string;
  rank: number;
  description: string;
  cropResult: ImageCropResult | null;
}

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, ImageCropUploadComponent],
  template: `
    <section class="bod-page">

      <!-- Page header -->
      <header class="bod-header">
        <div class="bod-header-text">
          <h1>Board of Directors</h1>
          <p>Manage the JCI Nusantara executive board per term year.</p>
        </div>
        <div class="bod-header-actions">
          <button class="btn-outline" (click)="openAddBoard()" [disabled]="saving">
            + Add Board Year
          </button>
        </div>
      </header>

      <!-- Feedback -->
      <div class="feedback success" *ngIf="successMsg">{{ successMsg }}</div>
      <div class="feedback error" *ngIf="errorMsg">{{ errorMsg }}</div>

      <!-- Loading -->
      <div class="loading-state" *ngIf="loading">
        <span class="spinner"></span> Loading board data…
      </div>

      <ng-container *ngIf="!loading">

        <!-- Empty state -->
        <div class="empty-state" *ngIf="boards.length === 0">
          <div class="empty-icon">🏛️</div>
          <h3>No board years yet</h3>
          <p>Add the first board year to get started.</p>
          <button class="btn-primary" (click)="openAddBoard()">+ Add Board Year</button>
        </div>

        <ng-container *ngIf="boards.length > 0">

          <!-- Year tabs -->
          <div class="year-tabs">
            <button
              *ngFor="let b of boards"
              class="year-tab"
              [class.active]="selectedBoard?.id === b.id"
              (click)="selectBoard(b)">
              {{ b.year }}
            </button>
          </div>

          <!-- Board content -->
          <div class="board-content" *ngIf="selectedBoard">

            <!-- Tagline bar -->
            <div class="tagline-bar">
              <div class="tagline-left">
                <span class="tagline-label">Theme</span>
                <ng-container *ngIf="!editingTagline">
                  <span class="tagline-value" *ngIf="selectedBoard.tagline">
                    "{{ selectedBoard.tagline }}"
                  </span>
                  <span class="tagline-empty" *ngIf="!selectedBoard.tagline">
                    No theme tagline set
                  </span>
                </ng-container>
                <input
                  *ngIf="editingTagline"
                  class="tagline-input"
                  [(ngModel)]="taglineDraft"
                  placeholder="e.g. Connect. Grow. Lead."
                  (keydown.enter)="saveTagline()"
                  (keydown.escape)="cancelTagline()" />
              </div>
              <div class="tagline-right">
                <ng-container *ngIf="!editingTagline">
                  <button class="btn-ghost" (click)="startEditTagline()">Edit theme</button>
                  <button class="btn-ghost danger" (click)="confirmDeleteBoard(selectedBoard)">
                    Delete {{ selectedBoard.year }}
                  </button>
                </ng-container>
                <ng-container *ngIf="editingTagline">
                  <button class="btn-primary sm" (click)="saveTagline()" [disabled]="saving">Save</button>
                  <button class="btn-ghost" (click)="cancelTagline()">Cancel</button>
                </ng-container>
              </div>
            </div>

            <!-- Members section -->
            <div class="members-section">
              <div class="members-header">
                <span class="members-count">
                  {{ members.length }} member{{ members.length !== 1 ? 's' : '' }}
                </span>
                <button class="btn-primary" (click)="openAddMember()">
                  + Add Member
                </button>
              </div>

              <!-- Members loading -->
              <div class="loading-state sm" *ngIf="membersLoading">
                <span class="spinner"></span> Loading members…
              </div>

              <!-- Members empty -->
              <div class="members-empty" *ngIf="!membersLoading && members.length === 0">
                <p>No members added yet for {{ selectedBoard.year }}.</p>
                <button class="btn-outline" (click)="openAddMember()">Add first member</button>
              </div>

              <!-- Members grid -->
              <div class="members-grid" *ngIf="!membersLoading && members.length > 0">
                <div class="member-card" *ngFor="let m of members">
                  <div class="member-photo">
                    <img
                      *ngIf="m.image || m.profileAvatar"
                      [src]="m.image ? photoUrl(m.image, m.crops) : photoUrl(m.profileAvatar!, m.profileAvatarCrops)"
                      [alt]="m.name"
                      (error)="onPhotoError($event)" />
                    <div class="member-photo-fallback" *ngIf="!m.image && !m.profileAvatar">
                      {{ initials(m.name) }}
                    </div>
                  </div>
                  <div class="member-body">
                    <div class="member-rank" *ngIf="m.rank">#{{ m.rank }}</div>
                    <div class="member-name">{{ m.name }}</div>
                    <div class="member-position">{{ m.position }}</div>
                  </div>
                  <div class="member-actions">
                    <button class="btn-ghost xs" (click)="openEditMember(m)">Edit</button>
                    <button class="btn-ghost xs danger" (click)="confirmDeleteMember(m)">Remove</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </ng-container>
      </ng-container>

    </section>

    <!-- ── Add Board Year overlay ───────────────────────────── -->
    <div class="overlay" *ngIf="boardFormOpen" (click)="closeBoardForm()">
      <div class="panel" (click)="$event.stopPropagation()">
        <div class="panel-head">
          <h3>Add Board Year</h3>
          <button class="panel-close" (click)="closeBoardForm()">✕</button>
        </div>
        <div class="panel-body">
          <label class="field-label">Year <span class="required">*</span></label>
          <input class="field-input" type="number" [(ngModel)]="boardDraft.year"
                 placeholder="e.g. 2026" min="2000" max="2099" />

          <label class="field-label" style="margin-top:16px">Theme tagline</label>
          <input class="field-input" type="text" [(ngModel)]="boardDraft.tagline"
                 placeholder="e.g. Connect. Grow. Lead." />
        </div>
        <div class="panel-foot">
          <button class="btn-ghost" (click)="closeBoardForm()">Cancel</button>
          <button class="btn-primary" (click)="saveBoard()" [disabled]="saving || !boardDraft.year">
            {{ saving ? 'Saving…' : 'Create Board Year' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Add / Edit Member overlay ──────────────────────────── -->
    <div class="overlay" *ngIf="memberFormOpen" (click)="closeMemberForm()">
      <div class="panel panel--wide" (click)="$event.stopPropagation()">
        <div class="panel-head">
          <h3>{{ editingMember ? 'Edit Member' : 'Add Member' }}</h3>
          <button class="panel-close" (click)="closeMemberForm()">✕</button>
        </div>
        <div class="panel-body">

          <!-- Photo crop upload -->
          <div class="field-section">
            <label class="field-label">Photo</label>
            <app-image-crop-upload
              [imageId]="editingMember?.image ?? null"
              [existingCrops]="editingMember?.crops ?? null"
              [modes]="['avatar']"
              (change)="memberForm.cropResult = $event">
            </app-image-crop-upload>
          </div>

          <div class="field-row">
            <div class="field-col">
              <label class="field-label">Full name <span class="required">*</span></label>
              <input class="field-input" type="text" [(ngModel)]="memberForm.name"
                     placeholder="e.g. Andrian Wijaya" />
            </div>
            <div class="field-col">
              <label class="field-label">Position <span class="required">*</span></label>
              <input class="field-input" type="text" [(ngModel)]="memberForm.position"
                     placeholder="e.g. Local President" />
            </div>
          </div>

          <div class="field-row">
            <div class="field-col field-col--sm">
              <label class="field-label">Rank / order</label>
              <input class="field-input" type="number" [(ngModel)]="memberForm.rank"
                     placeholder="1" min="1" />
              <span class="field-hint">Used for display order (1 = first)</span>
            </div>
          </div>

          <label class="field-label" style="margin-top:16px">Bio / description</label>
          <textarea class="field-input" rows="3" [(ngModel)]="memberForm.description"
                    placeholder="Short bio or description…"></textarea>
        </div>
        <div class="panel-foot">
          <button class="btn-ghost" (click)="closeMemberForm()">Cancel</button>
          <button class="btn-primary"
                  (click)="saveMember()"
                  [disabled]="saving || !memberForm.name || !memberForm.position">
            {{ saving ? 'Saving…' : (editingMember ? 'Save changes' : 'Add member') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Delete confirm overlay ─────────────────────────────── -->
    <div class="overlay" *ngIf="deleteTarget" (click)="cancelDelete()">
      <div class="panel panel--sm" (click)="$event.stopPropagation()">
        <div class="panel-head">
          <h3>Confirm deletion</h3>
        </div>
        <div class="panel-body">
          <p *ngIf="deleteType === 'member'">
            Remove <strong>{{ deleteTarget['name'] }}</strong> from the
            {{ selectedBoard?.year }} board? This cannot be undone.
          </p>
          <p *ngIf="deleteType === 'board'">
            Delete the <strong>{{ deleteTarget['year'] }}</strong> board year and
            all its members? This cannot be undone.
          </p>
        </div>
        <div class="panel-foot">
          <button class="btn-ghost" (click)="cancelDelete()">Cancel</button>
          <button class="btn-danger" (click)="executeDelete()" [disabled]="saving">
            {{ saving ? 'Deleting…' : 'Yes, delete' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .bod-page {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    /* Header */
    .bod-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
    }
    .bod-header h1 {
      margin: 0 0 4px;
      font-size: 1.75rem;
      font-weight: 700;
      color: var(--app-ink-strong);
    }
    .bod-header p {
      margin: 0;
      color: var(--app-muted);
      font-size: 0.9rem;
    }
    .bod-header-actions { display: flex; gap: 10px; flex-shrink: 0; padding-top: 4px; }

    /* Feedback */
    .feedback {
      padding: 12px 16px;
      border-radius: var(--app-radius);
      font-size: 0.875rem;
      font-weight: 500;
    }
    .feedback.success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
    .feedback.error   { background: #fff1f2; color: #991b1b; border: 1px solid #fecaca; }

    /* Loading */
    .loading-state {
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--app-muted);
      font-size: 0.9rem;
      padding: 32px 0;
    }
    .loading-state.sm { padding: 16px 0; }
    .spinner {
      width: 18px; height: 18px;
      border: 2px solid var(--app-border);
      border-top-color: var(--jci-blue);
      border-radius: 50%;
      display: inline-block;
      animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* Empty */
    .empty-state {
      text-align: center;
      padding: 64px 32px;
      color: var(--app-muted);
      border: 1px dashed var(--app-border);
      border-radius: var(--app-radius);
      background: var(--app-surface-soft);
    }
    .empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
    .empty-state h3 { margin: 0 0 8px; color: var(--app-text); }
    .empty-state p  { margin: 0 0 20px; font-size: 0.9rem; }

    /* Year tabs */
    .year-tabs {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
    }
    .year-tab {
      padding: 7px 20px;
      border-radius: 999px;
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      color: var(--app-muted);
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: all 140ms;
    }
    .year-tab:hover  { border-color: var(--jci-blue); color: var(--jci-blue); }
    .year-tab.active {
      background: var(--jci-blue);
      border-color: var(--jci-blue);
      color: #fff;
    }

    /* Board content */
    .board-content { display: flex; flex-direction: column; gap: 20px; }

    /* Tagline bar */
    .tagline-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 16px 20px;
      border-radius: var(--app-radius);
      border: 1px solid var(--app-border);
      background: var(--app-surface);
    }
    .tagline-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
    .tagline-label {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--app-muted);
      flex-shrink: 0;
    }
    .tagline-value {
      font-size: 1rem;
      font-weight: 600;
      color: var(--app-ink-strong);
      font-style: italic;
    }
    .tagline-empty { font-size: 0.9rem; color: var(--app-muted); }
    .tagline-input {
      border: 1px solid var(--jci-blue);
      border-radius: 8px;
      padding: 6px 12px;
      font-size: 0.95rem;
      font-family: inherit;
      color: var(--app-text);
      background: var(--app-surface);
      min-width: 280px;
      outline: none;
    }
    .tagline-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

    /* Members */
    .members-section { display: flex; flex-direction: column; gap: 16px; }
    .members-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .members-count {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--app-muted);
    }
    .members-empty {
      text-align: center;
      padding: 40px;
      color: var(--app-muted);
      font-size: 0.9rem;
      border: 1px dashed var(--app-border);
      border-radius: var(--app-radius);
      background: var(--app-surface-soft);
    }
    .members-empty p { margin: 0 0 16px; }

    /* Members grid */
    .members-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 16px;
    }
    .member-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 20px 16px 14px;
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      background: var(--app-surface);
      box-shadow: var(--app-shadow);
      text-align: center;
      transition: box-shadow 140ms;
    }
    .member-card:hover { box-shadow: 0 4px 16px rgba(19,15,45,0.09); }

    .member-photo {
      width: 80px; height: 80px;
      border-radius: 50%;
      overflow: hidden;
      flex-shrink: 0;
      border: 2px solid var(--app-border);
    }
    .member-photo img {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
    }
    .member-photo-fallback {
      width: 100%; height: 100%;
      background: var(--jci-blue);
      color: #fff;
      font-size: 1.3rem;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .member-body { display: flex; flex-direction: column; gap: 3px; width: 100%; }
    .member-rank {
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      color: var(--app-muted);
    }
    .member-name {
      font-size: 0.9rem;
      font-weight: 700;
      color: var(--app-ink-strong);
      line-height: 1.25;
    }
    .member-position {
      font-size: 0.78rem;
      color: var(--jci-blue);
      font-weight: 600;
    }

    .member-actions {
      display: flex;
      gap: 6px;
      width: 100%;
      justify-content: center;
      padding-top: 4px;
      border-top: 1px solid var(--app-border);
    }

    /* Buttons */
    .btn-primary {
      padding: 9px 18px;
      border-radius: 999px;
      border: none;
      background: var(--jci-blue);
      color: #fff;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: opacity 140ms;
    }
    .btn-primary:hover:not(:disabled)  { opacity: 0.88; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-primary.sm { padding: 6px 14px; font-size: 0.82rem; }

    .btn-outline {
      padding: 9px 18px;
      border-radius: 999px;
      border: 1px solid var(--app-border);
      background: var(--app-surface);
      color: var(--app-text);
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: border-color 140ms, color 140ms;
    }
    .btn-outline:hover:not(:disabled) { border-color: var(--jci-blue); color: var(--jci-blue); }
    .btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-outline.sm { padding: 6px 14px; font-size: 0.82rem; }

    .btn-ghost {
      padding: 7px 14px;
      border-radius: 999px;
      border: 1px solid transparent;
      background: transparent;
      color: var(--app-muted);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      transition: background 140ms, color 140ms;
    }
    .btn-ghost:hover { background: var(--app-surface-soft); color: var(--app-text); }
    .btn-ghost.danger { color: var(--app-danger); }
    .btn-ghost.danger:hover { background: #fff1f2; }
    .btn-ghost.xs { padding: 4px 10px; font-size: 0.78rem; }

    .btn-danger {
      padding: 9px 18px;
      border-radius: 999px;
      border: none;
      background: var(--app-danger);
      color: #fff;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: opacity 140ms;
    }
    .btn-danger:hover:not(:disabled) { opacity: 0.88; }
    .btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Overlay */
    .overlay {
      position: fixed;
      inset: 0;
      background: rgba(19, 15, 45, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 100;
      padding: 24px;
      backdrop-filter: blur(2px);
    }
    .panel {
      background: var(--app-surface);
      border-radius: 14px;
      box-shadow: 0 24px 48px rgba(19,15,45,0.18);
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 480px;
      max-height: 90vh;
      overflow: hidden;
    }
    .panel--wide { max-width: 620px; }
    .panel--sm   { max-width: 380px; }
    .panel-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px 16px;
      border-bottom: 1px solid var(--app-border);
    }
    .panel-head h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 700;
      color: var(--app-ink-strong);
    }
    .panel-close {
      width: 28px; height: 28px;
      border-radius: 50%;
      border: 1px solid var(--app-border);
      background: transparent;
      color: var(--app-muted);
      cursor: pointer;
      font-size: 13px;
      display: flex; align-items: center; justify-content: center;
      line-height: 1;
      font-family: inherit;
    }
    .panel-close:hover { background: var(--app-surface-soft); color: var(--app-text); }
    .panel-body {
      padding: 24px;
      overflow-y: auto;
      flex: 1;
    }
    .panel-body p { margin: 0; color: var(--app-text); font-size: 0.9rem; line-height: 1.5; }
    .panel-foot {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 16px 24px;
      border-top: 1px solid var(--app-border);
    }

    /* Form fields */
    .field-label {
      display: block;
      font-size: 0.82rem;
      font-weight: 600;
      color: var(--app-text);
      margin-bottom: 6px;
    }
    .required { color: var(--app-danger); }
    .field-input {
      width: 100%;
      box-sizing: border-box;
      padding: 9px 12px;
      border: 1px solid var(--app-border);
      border-radius: 8px;
      background: var(--app-input-bg);
      color: var(--app-text);
      font-size: 0.9rem;
      font-family: inherit;
      outline: none;
      transition: border-color 140ms;
    }
    .field-input:focus { border-color: var(--jci-blue); background: var(--app-surface); }
    textarea.field-input { resize: vertical; min-height: 80px; }
    .field-hint { font-size: 0.75rem; color: var(--app-muted); margin-top: 4px; display: block; }
    .field-row { display: flex; gap: 16px; margin-top: 16px; }
    .field-col { flex: 1; min-width: 0; }
    .field-col--sm { max-width: 160px; }

    /* Photo upload row */
    .photo-upload-row {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 16px;
      border: 1px solid var(--app-border);
      border-radius: 10px;
      background: var(--app-surface-soft);
      margin-bottom: 8px;
    }
    .photo-preview {
      width: 72px; height: 72px;
      border-radius: 50%;
      overflow: hidden;
      border: 2px solid var(--app-border);
      flex-shrink: 0;
    }
    .photo-preview img {
      width: 100%; height: 100%;
      object-fit: cover; display: block;
    }
    .photo-placeholder {
      width: 100%; height: 100%;
      background: var(--app-surface-soft);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      font-weight: 700;
      color: var(--app-muted);
      border: 2px dashed var(--app-border);
      border-radius: 50%;
    }
    .photo-upload-info { display: flex; flex-direction: column; gap: 6px; }
    .file-label {
      display: inline-block;
      cursor: pointer;
      font-size: 0.82rem;
      max-width: 200px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `]
})
export class BoardOfDirectorsComponent implements OnInit {
  boards: Board[] = [];
  selectedBoard: Board | null = null;
  members: BoardMember[] = [];

  loading = true;
  membersLoading = false;
  saving = false;
  successMsg = '';
  errorMsg = '';

  // Tagline editing
  editingTagline = false;
  taglineDraft = '';

  // Board year form
  boardFormOpen = false;
  boardDraft: { year: number | null; tagline: string } = { year: null, tagline: '' };

  // Member form
  memberFormOpen = false;
  editingMember: BoardMember | null = null;
  memberForm: MemberForm = this.emptyMemberForm();

  // Delete confirm
  deleteTarget: Record<string, unknown> | null = null;
  deleteType: 'member' | 'board' = 'member';

  constructor(private readonly directus: DirectusService) {}

  async ngOnInit(): Promise<void> {
    await this.loadBoards();
  }

  private async loadBoards(): Promise<void> {
    this.loading = true;
    this.clearMessages();
    try {
      const raw = await this.directus.list('lbods', {
        sort: '-year',
        limit: 50
      });
      this.boards = raw.map(this.toBoard);
      if (this.boards.length > 0) {
        await this.selectBoard(this.boards[0]);
      }
    } catch {
      this.errorMsg = 'Failed to load board data.';
    } finally {
      this.loading = false;
    }
  }

  async selectBoard(board: Board): Promise<void> {
    this.selectedBoard = board;
    this.editingTagline = false;
    await this.loadMembers(board.id);
  }

  private async loadMembers(boardId: number): Promise<void> {
    this.membersLoading = true;
    try {
      const year = this.selectedBoard?.year;
      const [rawMembers, rawPresidents] = await Promise.all([
        this.directus.list('lbodmembers', {
          'filter[lbod_id][_eq]': boardId,
          sort: 'rank,name',
          limit: 200
        }),
        year
          ? this.directus.list('presidents', {
              'filter[year][_eq]': year,
              'fields': 'id,name,profile_id,image,crops',
              limit: 10
            })
          : Promise.resolve([])
      ]);

      // Build president lookup: profile_id / name → { image, crops }
      type PresLookup = { image: string; crops: CropsRecord | null };
      const byProfileId = new Map<string, PresLookup>();
      const byName      = new Map<string, PresLookup>();
      for (const p of rawPresidents) {
        const img = p['image'] as string | null;
        if (!img) continue;
        const entry: PresLookup = { image: img, crops: (p['crops'] as CropsRecord) ?? null };
        const pid   = p['profile_id'] as string | null;
        const pname = (p['name'] as string | null)?.trim().toLowerCase();
        if (pid)   byProfileId.set(pid, entry);
        if (pname) byName.set(pname, entry);
      }

      this.members = rawMembers.map(r => {
        const m = this.toMember(r);
        if (!m.image) {
          const pres =
            (m.profile_id ? byProfileId.get(m.profile_id) : undefined) ??
            byName.get(m.name.trim().toLowerCase());
          if (pres) {
            m.profileAvatar      = pres.image;
            m.profileAvatarCrops = pres.crops;
          }
        }
        return m;
      });
    } catch {
      this.errorMsg = 'Failed to load members.';
    } finally {
      this.membersLoading = false;
    }
  }

  // ── Tagline ────────────────────────────────────────────────────

  startEditTagline(): void {
    this.taglineDraft = this.selectedBoard?.tagline ?? '';
    this.editingTagline = true;
  }

  cancelTagline(): void {
    this.editingTagline = false;
  }

  async saveTagline(): Promise<void> {
    if (!this.selectedBoard || this.saving) return;
    this.saving = true;
    this.clearMessages();
    try {
      await this.directus.update('lbods', this.selectedBoard.id, {
        tagline: this.taglineDraft.trim() || null
      });
      this.selectedBoard = { ...this.selectedBoard, tagline: this.taglineDraft.trim() || null };
      this.boards = this.boards.map(b =>
        b.id === this.selectedBoard!.id ? this.selectedBoard! : b
      );
      this.editingTagline = false;
      this.showSuccess('Theme tagline updated.');
    } catch {
      this.errorMsg = 'Failed to save tagline.';
    } finally {
      this.saving = false;
    }
  }

  // ── Board year form ────────────────────────────────────────────

  openAddBoard(): void {
    this.boardDraft = { year: new Date().getFullYear(), tagline: '' };
    this.boardFormOpen = true;
    this.clearMessages();
  }

  closeBoardForm(): void {
    this.boardFormOpen = false;
  }

  async saveBoard(): Promise<void> {
    if (!this.boardDraft.year || this.saving) return;
    this.saving = true;
    this.clearMessages();
    try {
      const created = await this.directus.create('lbods', {
        year: this.boardDraft.year,
        tagline: this.boardDraft.tagline.trim() || null
      });
      const newBoard = this.toBoard(created);
      this.boards = [newBoard, ...this.boards].sort((a, b) => b.year - a.year);
      this.boardFormOpen = false;
      await this.selectBoard(newBoard);
      this.showSuccess(`Board year ${newBoard.year} created.`);
    } catch {
      this.errorMsg = 'Failed to create board year.';
    } finally {
      this.saving = false;
    }
  }

  // ── Member form ────────────────────────────────────────────────

  openAddMember(): void {
    this.editingMember = null;
    this.memberForm = this.emptyMemberForm();
    this.memberFormOpen = true;
    this.clearMessages();
  }

  openEditMember(member: BoardMember): void {
    this.editingMember = member;
    this.memberForm = {
      name: member.name,
      position: member.position,
      rank: member.rank,
      description: member.description ?? '',
      cropResult: null
    };
    this.memberFormOpen = true;
    this.clearMessages();
  }

  closeMemberForm(): void {
    this.memberFormOpen = false;
    this.editingMember = null;
  }

  async saveMember(): Promise<void> {
    if (!this.selectedBoard || this.saving) return;
    if (!this.memberForm.name.trim() || !this.memberForm.position.trim()) return;
    this.saving = true;
    this.clearMessages();

    try {
      let imageId: string | null = this.editingMember?.image ?? null;
      let crops: CropsRecord | null = this.editingMember?.crops ?? null;

      const cr = this.memberForm.cropResult;
      if (cr?.file) {
        const uploaded = await this.directus.uploadFile(cr.file);
        imageId = uploaded['id'] as string;
      }
      if (cr?.crops) crops = cr.crops;

      const payload: Record<string, unknown> = {
        name: this.memberForm.name.trim(),
        position: this.memberForm.position.trim(),
        rank: this.memberForm.rank || null,
        description: this.memberForm.description.trim() || null,
        lbod_id: this.selectedBoard.id,
        year: this.selectedBoard.year,
        image: imageId,
        crops
      };

      if (this.editingMember) {
        await this.directus.update('lbodmembers', this.editingMember.id, payload);
        this.showSuccess('Member updated.');
      } else {
        await this.directus.create('lbodmembers', payload);
        this.showSuccess('Member added.');
      }

      this.memberFormOpen = false;
      this.editingMember = null;
      await this.loadMembers(this.selectedBoard.id);
    } catch {
      this.errorMsg = 'Failed to save member.';
    } finally {
      this.saving = false;
    }
  }

  // ── Delete ─────────────────────────────────────────────────────

  confirmDeleteMember(member: BoardMember): void {
    this.deleteTarget = member as unknown as Record<string, unknown>;
    this.deleteType = 'member';
  }

  confirmDeleteBoard(board: Board): void {
    this.deleteTarget = board as unknown as Record<string, unknown>;
    this.deleteType = 'board';
  }

  cancelDelete(): void {
    this.deleteTarget = null;
  }

  async executeDelete(): Promise<void> {
    if (!this.deleteTarget || this.saving) return;
    this.saving = true;
    this.clearMessages();
    try {
      if (this.deleteType === 'member') {
        await this.directus.remove('lbodmembers', this.deleteTarget['id'] as number);
        this.members = this.members.filter(m => m.id !== this.deleteTarget!['id']);
        this.showSuccess('Member removed.');
      } else {
        await this.directus.remove('lbods', this.deleteTarget['id'] as number);
        this.boards = this.boards.filter(b => b.id !== this.deleteTarget!['id']);
        this.selectedBoard = this.boards[0] ?? null;
        if (this.selectedBoard) await this.loadMembers(this.selectedBoard.id);
        else this.members = [];
        this.showSuccess('Board year deleted.');
      }
      this.deleteTarget = null;
    } catch {
      this.errorMsg = 'Failed to delete.';
      this.deleteTarget = null;
    } finally {
      this.saving = false;
    }
  }

  // ── Helpers ────────────────────────────────────────────────────

  photoUrl(fileId: string, crops?: CropsRecord | null): string {
    return this.directus.croppedAssetUrl(fileId, crops?.['avatar'], 200, 200);
  }

  initials(name: string): string {
    return name.trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  }

  onPhotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  private emptyMemberForm(): MemberForm {
    return { name: '', position: '', rank: 0, description: '', cropResult: null };
  }

  private toBoard = (r: Record<string, unknown>): Board => ({
    id: r['id'] as number,
    year: r['year'] as number,
    tagline: (r['tagline'] as string) ?? null,
    tagline_image: (r['tagline_image'] as string) ?? null
  });

  private toMember = (r: Record<string, unknown>): BoardMember => ({
    id: r['id'] as number,
    name: (r['name'] as string) ?? '',
    position: (r['position'] as string) ?? '',
    image: (r['image'] as string) ?? null,
    profileAvatar: null,
    profileAvatarCrops: null,
    crops: (r['crops'] as CropsRecord) ?? null,
    rank: (r['rank'] as number) ?? 0,
    lbod_id: r['lbod_id'] as number,
    profile_id: (r['profile_id'] as string) ?? null,
    description: (r['description'] as string) ?? null
  });

  private showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => (this.successMsg = ''), 3500);
  }

  private clearMessages(): void {
    this.successMsg = '';
    this.errorMsg = '';
  }
}
